import { Network } from '@shared/types'
import { sharedConvertTimestampToMilliseconds } from '@shared/utils/formatter'
import { transactionFailedStates } from '@/app/data/transaction'
import { TransactionState } from '@/types'
import type { PeggyDepositTx, PeggyWithdrawalTx } from '@injectivelabs/sdk-ts'
import type {
  UserDeposit,
  UiTransaction,
  PeggyTransaction,
  UiPendingTransactionWithKey
} from '@/types'

export const mapPeggyDepositToPeggo = ({
  peggoDeposits,
  pendingTransaction
}: {
  peggoDeposits: UserDeposit[]
  pendingTransaction: UiPendingTransactionWithKey
}): UiPendingTransactionWithKey => {
  if (pendingTransaction.state !== TransactionState.Submitted) {
    return pendingTransaction
  }

  if (
    !pendingTransaction.isDeposit ||
    pendingTransaction.network !== Network.Ethereum
  ) {
    return pendingTransaction
  }

  const updatedTransaction = peggoDeposits.find(({ id }) =>
    id.includes(pendingTransaction.txHash)
  )

  if (!updatedTransaction) {
    return pendingTransaction
  }

  return {
    ...pendingTransaction,
    nonce: updatedTransaction.eventNonce,
    state: TransactionState.EthereumConfirming,
    blockHeight: updatedTransaction.blockHeight,
    timestamp: sharedConvertTimestampToMilliseconds(
      updatedTransaction.timestamp
    )
  }
}

export const mapPeggyDepositToInjectiveTransaction = ({
  transactions,
  pendingTransaction
}: {
  transactions: UiTransaction[]
  pendingTransaction: UiPendingTransactionWithKey
}): UiPendingTransactionWithKey => {
  if (
    ![
      TransactionState.EthereumConfirming,
      TransactionState.InjectiveConfirming
    ].includes(pendingTransaction.state)
  ) {
    return pendingTransaction
  }

  if (
    !pendingTransaction.isDeposit ||
    pendingTransaction.network !== Network.Ethereum
  ) {
    return pendingTransaction
  }

  const updatedTransaction = transactions.find(
    ({ network, nonce }) =>
      network === pendingTransaction.network &&
      nonce === pendingTransaction.nonce
  )

  if (!updatedTransaction) {
    return pendingTransaction
  }

  // only show explorerLink on step 1 & 3
  const explorerLinkMap = [
    TransactionState.Completed,
    TransactionState.EthereumConfirming
  ].includes(updatedTransaction.state)
    ? pendingTransaction.explorerLinkMap
    : {
        ...pendingTransaction.explorerLinkMap,
        [updatedTransaction.state]: updatedTransaction.explorerLink
      }

  return {
    ...pendingTransaction,
    explorerLinkMap,
    state: updatedTransaction.state,
    timestamp: updatedTransaction.timestamp,
    explorerLink: updatedTransaction.explorerLink
  }
}

export const mapPeggyWithdrawalToInjectiveTransaction = ({
  transactions,
  pendingTransaction
}: {
  transactions: UiTransaction[]
  pendingTransaction: UiPendingTransactionWithKey
}): UiPendingTransactionWithKey => {
  if (pendingTransaction.state !== TransactionState.Submitted) {
    return pendingTransaction
  }

  if (
    pendingTransaction.isDeposit ||
    pendingTransaction.network !== Network.Ethereum
  ) {
    return pendingTransaction
  }

  const updatedTransaction = transactions.find(
    ({ state, network, txHashes }) =>
      network === pendingTransaction.network &&
      (txHashes || []).find((hash) =>
        hash.includes(pendingTransaction.txHash.toLowerCase())
      ) &&
      state === TransactionState.Completed
  )

  if (!updatedTransaction) {
    return pendingTransaction
  }

  return {
    ...pendingTransaction,
    state: updatedTransaction.state,
    nonce: updatedTransaction.nonce,
    timestamp: updatedTransaction.timestamp,
    explorerLink: updatedTransaction.explorerLink,
    explorerLinkMap: {
      ...pendingTransaction.explorerLinkMap,
      [updatedTransaction.state]: updatedTransaction.explorerLink
    }
  }
}

export const peggyDepositTxToPeggyTransaction = (
  tx: PeggyDepositTx,
  peggyUserDeposits: UserDeposit[]
): PeggyTransaction => {
  const isFailedOrCancelled = transactionFailedStates.includes(
    tx.state as TransactionState
  )

  const peggoUserDeposit = peggyUserDeposits.find(
    ({ eventNonce }) => eventNonce === tx.eventNonce
  )

  const ethTxHash = peggoUserDeposit?.id.split('-')[0] || ''

  return {
    ethTxHash,
    txHash: isFailedOrCancelled ? tx.txHashesList.pop() : tx.txHashesList[0],
    isDeposit: true,
    denom: tx.denom,
    amount: tx.amount,
    sender: tx.sender,
    nonce: tx.eventNonce,
    receiver: tx.receiver,
    blockHeight: tx.eventHeight,
    txHashesList: tx.txHashesList,
    timestamp: Date.parse(tx.createdAt),
    state: tx.state as TransactionState
  }
}

export const peggyWithdrawalTxToPeggyTransaction = (
  tx: PeggyWithdrawalTx
): PeggyTransaction => {
  const failedOrCancelled = transactionFailedStates.includes(
    tx.state as TransactionState
  )

  return {
    txHash: failedOrCancelled ? tx.txHashesList.pop() : tx.txHashesList[0],
    isDeposit: false,
    denom: tx.denom,
    amount: tx.amount,
    sender: tx.sender,
    nonce: tx.eventNonce,
    receiver: tx.receiver,
    bridgeFee: tx.bridgeFee,
    blockHeight: tx.eventHeight,
    txHashesList: tx.txHashesList,
    ethBlockHeight: tx.eventHeight,
    timestamp: Date.parse(tx.createdAt),
    state: tx.state as TransactionState
  }
}
